import type {
  AiArtifact,
  AiAsset,
  AiConfidence,
  AiDimension,
  AiEffort,
  AiRisk,
  AiRiskSeverity,
  AiStep,
  AiVerdict,
  ArtifactKind,
} from './types';
import { ARTIFACT_KINDS } from './types';
import { clampScore, inlineText, truncate } from './format';

/**
 * AI 研判契约 —— 提示词与产物解析的唯一事实来源。
 *
 * 提示词构造与产物解析都写成**纯函数**，因此可以离线单测：
 * 模型换版本时，先跑 `tests/ai-prompt.test.ts` 与 `tests/ai-parse.test.ts`，
 * 确认契约没被破坏，再上线。
 */

/** 提示词版本。任何会影响模型输出的改动都必须递增它，并写进 run 记录。 */
export const AI_PROMPT_VERSION = 'v1';

/** 报告的五维评分。模型的输出按 key 对齐，顺序即界面顺序。 */
export const AI_DIMENSIONS: readonly { key: string; label: string }[] = [
  { key: 'value', label: '用户价值' },
  { key: 'feasibility', label: '技术可行性' },
  { key: 'cost', label: '成本与投入' },
  { key: 'risk', label: '风险可控度' },
  { key: 'differentiation', label: '差异化与复用' },
];

export const ARTIFACT_KIND_LABELS: Readonly<Record<ArtifactKind, string>> = {
  prd: 'PRD 大纲',
  api: '接口设计',
  data_model: '数据模型',
  code: '关键代码片段',
  experiment: '最小验证方案',
};

export const ARTIFACT_KIND_HINTS: Readonly<Record<ArtifactKind, string>> = {
  prd: 'Markdown 大纲：背景、目标用户、核心场景、功能范围（做什么 / 不做什么）、验收标准。',
  api: '接口清单：方法、路径、请求体、响应体、错误码。若不需要接口则说明理由。',
  data_model: '表结构：字段名、类型、约束、索引、关系；用 SQL 或表格描述均可。',
  code: '关键路径的最小代码片段：只写决定成败的那几十行，不要整文件。',
  experiment: '最小验证方案：假设、验证方式、样本量、判定标准、预计投入与周期。',
};

export const AI_LIMITS = {
  /** 注入提示词的团队能力清单上限。 */
  capabilitiesChars: 4000,
  /** 注入提示词的创意正文上限。 */
  bodyChars: 4000,
  /** 单个产物段落落库上限，防止把整份文档塞进一行。 */
  artifactChars: 12000,
  maxRisks: 8,
  maxAssets: 8,
  maxSteps: 8,
  maxQuestions: 8,
  maxArtifacts: 5,
} as const;

export interface ChatMessage {
  role: 'system' | 'user';
  content: string;
}

export interface PromptIdeaInput {
  title: string;
  summary: string;
  body: string;
  hypothesis: string;
  audience: string;
  successMetric: string;
  tags: string[];
}

export interface PromptContext {
  /** 团队能力清单：现有系统、技术栈、可复用资产。没有时传空串。 */
  capabilities: string;
  /** 平台上的分类标签，帮助模型归位。 */
  tags: readonly string[];
  authorName: string;
}

const SYSTEM_PROMPT = [
  '你是 Havorix（一家做低空经济与无人机运营监管的技术公司）的内部创意评审助手。',
  '你的任务不是替团队做决定，而是在几分钟内把「值不值得投入第一轮验证」这件事说清楚。',
  '',
  '工作要求：',
  '1. 只依据用户提供的信息与团队能力清单判断，不要编造用户数据、市场规模或竞品细节。',
  '2. 信息不足时结论取 needs_more_info，并在 openQuestions 里列出**具体要补什么**，不要追问泛泛的问题。',
  '3. reusableAssets 只能引用能力清单里真实存在的系统、模块或技术栈；没有可复用的就返回空数组。',
  '4. mvpSteps 必须是能在一到两周内跑完的最小验证，禁止把完整产品拆成步骤。',
  '5. artifacts 必须给到可评审的草稿程度，不是要点罗列。代码片段只写决定成败的关键路径。',
  '6. 全部输出使用简体中文，正式书面语，不使用 emoji 与感叹号。',
  '7. 只输出一个 JSON 对象，不要包裹 Markdown 代码块，不要输出任何解释性文字。',
].join('\n');

function schemaBlock(): string {
  const dimensions = AI_DIMENSIONS.map((dimension) => `    { "key": "${dimension.key}", "label": "${dimension.label}", "score": 0-100 的整数, "note": "一句话理由" }`).join(',\n');
  const artifacts = ARTIFACT_KINDS.map(
    (kind) => `    { "kind": "${kind}", "title": "${ARTIFACT_KIND_LABELS[kind]}", "language": null 或代码语言, "content": "${ARTIFACT_KIND_LABELS[kind]}正文" }`,
  ).join(',\n');

  return [
    '严格按下面的 JSON 结构输出，字段一个都不能少：',
    '{',
    '  "verdict": "go" | "needs_more_info" | "no_go",',
    '  "score": 0-100 的整数（综合分）,',
    '  "confidence": "high" | "medium" | "low",',
    '  "summary": "三到五句话的结论，先说判断，再说关键理由",',
    '  "dimensions": [',
    dimensions,
    '  ],',
    '  "risks": [',
    '    { "title": "风险名", "severity": "high" | "medium" | "low", "detail": "为什么会发生", "mitigation": "怎么规避" }',
    '  ],',
    '  "reusableAssets": [',
    '    { "name": "能力清单里的名称", "kind": "系统 / 模块 / 技术栈", "note": "怎么复用" }',
    '  ],',
    '  "mvpSteps": [',
    '    { "order": 1, "title": "步骤名", "detail": "怎么做，做到什么程度算完成", "effort": "S" | "M" | "L" }',
    '  ],',
    '  "artifacts": [',
    artifacts,
    '  ],',
    '  "openQuestions": ["需要人回答的具体问题"]',
    '}',
    '',
    '说明：',
    `- dimensions 必须恰好包含这五个 key，顺序不变：${AI_DIMENSIONS.map((dimension) => dimension.key).join('、')}。`,
    `- artifacts 的 kind 只能取：${ARTIFACT_KINDS.join('、')}；确实产不出的段落可以省略，但至少要给出 experiment。`,
    '- risks / reusableAssets / mvpSteps / openQuestions 在没有内容时返回空数组，不要编造。',
  ].join('\n');
}

/**
 * 构造研判提示词。
 *
 * 团队能力清单是这里最关键的一段：没有它，模型会建议团队去造已经有的轮子。
 */
export function buildValidationPrompt(idea: PromptIdeaInput, context: PromptContext): ChatMessage[] {
  const capabilities = context.capabilities.trim();
  const tags = context.tags.filter((tag) => tag.trim() !== '');

  const userLines = [
    '## 待研判的创意',
    `标题：${inlineText(idea.title)}`,
    `一句话说明：${inlineText(idea.summary)}`,
    `核心假设：${inlineText(idea.hypothesis)}`,
    `面向谁：${inlineText(idea.audience)}`,
    `用什么指标衡量成功：${inlineText(idea.successMetric)}`,
    `提出人：${inlineText(context.authorName) || '未标注'}`,
    `标签：${idea.tags.length > 0 ? idea.tags.join('、') : '无'}`,
    '',
    '## 详细说明',
    truncate(idea.body.trim(), AI_LIMITS.bodyChars) || '（作者未补充详细说明）',
    '',
    '## 团队能力清单',
    capabilities === '' ? '（管理员尚未填写能力清单，请只依据创意信息判断，并在 openQuestions 中要求补充团队现状）' : truncate(capabilities, AI_LIMITS.capabilitiesChars),
    '',
    '## 平台现有分类标签',
    tags.length > 0 ? tags.join('、') : '（暂无）',
    '',
    '## 输出',
    schemaBlock(),
  ];

  return [
    { role: 'system', content: SYSTEM_PROMPT },
    { role: 'user', content: userLines.join('\n') },
  ];
}

export interface AiReportContent {
  verdict: AiVerdict;
  score: number;
  confidence: AiConfidence;
  summary: string;
  dimensions: AiDimension[];
  risks: AiRisk[];
  reusableAssets: AiAsset[];
  mvpSteps: AiStep[];
  artifacts: AiArtifact[];
  openQuestions: string[];
}

export type ParseOutcome = { ok: true; content: AiReportContent } | { ok: false; reason: string; raw: string };

const VERDICTS: readonly AiVerdict[] = ['go', 'needs_more_info', 'no_go'];
const CONFIDENCES: readonly AiConfidence[] = ['high', 'medium', 'low'];
const SEVERITIES: readonly AiRiskSeverity[] = ['high', 'medium', 'low'];
const EFFORTS: readonly AiEffort[] = ['S', 'M', 'L'];

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asText(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return '';
}

/**
 * 从模型输出里抠出 JSON 对象。
 *
 * 顺序：直接 parse → 去掉 ```json 围栏 → 取第一个 `{` 到最后一个 `}` 的片段。
 * 模型被要求纯 JSON 输出，但真实世界会漂移，这里做最小宽容。
 */
export function extractJsonObject(text: string): Record<string, unknown> | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;

  const direct = tryParseObject(trimmed);
  if (direct) return direct;

  const withoutFence = trimmed
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();
  if (withoutFence !== trimmed) {
    const fenced = tryParseObject(withoutFence);
    if (fenced) return fenced;
  }

  const start = withoutFence.indexOf('{');
  const end = withoutFence.lastIndexOf('}');
  if (start === -1 || end <= start) return null;
  return tryParseObject(withoutFence.slice(start, end + 1));
}

function tryParseObject(text: string): Record<string, unknown> | null {
  try {
    return asRecord(JSON.parse(text));
  } catch {
    return null;
  }
}

function normalizeDimensions(raw: unknown, overall: number): AiDimension[] {
  const items = asArray(raw).map(asRecord);
  return AI_DIMENSIONS.map((dimension) => {
    const match = items.find((item) => item !== null && asText(item['key']) === dimension.key) ?? null;
    if (!match) {
      return { key: dimension.key, label: dimension.label, score: overall, note: '模型未单独给出该维度评分，已按综合分填充。' };
    }
    const score = match['score'];
    return {
      key: dimension.key,
      label: dimension.label,
      score: score === undefined || score === null ? overall : clampScore(score),
      note: asText(match['note']),
    };
  });
}

function normalizeSeverity(value: unknown): AiRiskSeverity {
  const text = asText(value).toLowerCase();
  return (SEVERITIES as readonly string[]).includes(text) ? (text as AiRiskSeverity) : 'medium';
}

function normalizeRisk(raw: unknown): AiRisk | null {
  const item = asRecord(raw);
  if (!item) return null;
  const title = asText(item['title']);
  if (title === '') return null;
  return {
    title: truncate(title, 120),
    severity: normalizeSeverity(item['severity']),
    detail: truncate(asText(item['detail']), 800),
    mitigation: truncate(asText(item['mitigation']), 800),
  };
}

function normalizeAsset(raw: unknown): AiAsset | null {
  const item = asRecord(raw);
  if (!item) return null;
  const name = asText(item['name']);
  if (name === '') return null;
  return { name: truncate(name, 120), kind: truncate(asText(item['kind']) || '资产', 40), note: truncate(asText(item['note']), 400) };
}

function normalizeEffort(value: unknown): AiEffort {
  const text = asText(value).toUpperCase();
  return (EFFORTS as readonly string[]).includes(text) ? (text as AiEffort) : 'M';
}

function normalizeStep(raw: unknown, index: number): AiStep | null {
  const item = asRecord(raw);
  if (!item) return null;
  const title = asText(item['title']);
  if (title === '') return null;
  const order = Number(item['order']);
  return {
    order: Number.isFinite(order) && order > 0 ? Math.round(order) : index + 1,
    title: truncate(title, 160),
    detail: truncate(asText(item['detail']), 1200),
    effort: normalizeEffort(item['effort']),
  };
}

function normalizeArtifactKind(value: unknown): ArtifactKind | null {
  const text = asText(value).toLowerCase().replace(/[\s-]+/g, '_');
  return (ARTIFACT_KINDS as readonly string[]).includes(text) ? (text as ArtifactKind) : null;
}

function normalizeArtifact(raw: unknown): AiArtifact | null {
  const item = asRecord(raw);
  if (!item) return null;
  const kind = normalizeArtifactKind(item['kind']);
  if (!kind) return null;
  const content = asText(item['content']) || asText(item['body']) || asText(item['markdown']);
  if (content === '') return null;
  const language = asText(item['language']);
  return {
    kind,
    title: truncate(asText(item['title']) || ARTIFACT_KIND_LABELS[kind], 120),
    language: language === '' ? null : truncate(language, 24),
    content: truncate(content, AI_LIMITS.artifactChars),
  };
}

function normalizeQuestion(raw: unknown): string | null {
  const text = typeof raw === 'string' ? raw.trim() : asText(asRecord(raw)?.['question']);
  return text === '' ? null : truncate(text, 400);
}

/**
 * 把模型的原始输出解析成报告内容。
 *
 * 判定失败的只有一种情况：抠不出 JSON，或者 `score` 不是有限数字。
 * 其余字段做归一化（缺维度按综合分填充、无效 kind 丢弃、长度截断），
 * 因为「格式漂移」不该让整次研判白跑；但「结论本身缺失」必须如实失败。
 */
export function parseReportContent(text: string): ParseOutcome {
  const raw = text.trim();
  const object = extractJsonObject(raw);
  if (!object) {
    return { ok: false, reason: 'unparseable_json', raw };
  }

  const scoreValue = object['score'];
  const scoreNumber = typeof scoreValue === 'number' ? scoreValue : Number(scoreValue);
  if (!Number.isFinite(scoreNumber)) {
    return { ok: false, reason: 'missing_score', raw };
  }
  const score = clampScore(scoreNumber);

  const verdictText = asText(object['verdict']).toLowerCase();
  const verdict: AiVerdict = (VERDICTS as readonly string[]).includes(verdictText)
    ? (verdictText as AiVerdict)
    : score >= 65
      ? 'go'
      : score >= 40
        ? 'needs_more_info'
        : 'no_go';

  const confidenceText = asText(object['confidence']).toLowerCase();
  const confidence: AiConfidence = (CONFIDENCES as readonly string[]).includes(confidenceText) ? (confidenceText as AiConfidence) : 'medium';

  const summary = truncate(asText(object['summary']), 2000) || `模型未给出结论摘要，综合分 ${score}。`;

  return {
    ok: true,
    content: {
      verdict,
      score,
      confidence,
      summary,
      dimensions: normalizeDimensions(object['dimensions'], score),
      risks: asArray(object['risks'])
        .map(normalizeRisk)
        .filter((risk): risk is AiRisk => risk !== null)
        .slice(0, AI_LIMITS.maxRisks),
      reusableAssets: asArray(object['reusableAssets'])
        .map(normalizeAsset)
        .filter((asset): asset is AiAsset => asset !== null)
        .slice(0, AI_LIMITS.maxAssets),
      mvpSteps: asArray(object['mvpSteps'])
        .map(normalizeStep)
        .filter((step): step is AiStep => step !== null)
        .sort((left, right) => left.order - right.order)
        .slice(0, AI_LIMITS.maxSteps),
      artifacts: asArray(object['artifacts'])
        .map(normalizeArtifact)
        .filter((artifact): artifact is AiArtifact => artifact !== null)
        .slice(0, AI_LIMITS.maxArtifacts),
      openQuestions: asArray(object['openQuestions'])
        .map(normalizeQuestion)
        .filter((question): question is string => question !== null)
        .slice(0, AI_LIMITS.maxQuestions),
    },
  };
}
